import { createDemoResult } from "./demoData";
import { ApiDebugError, type AnalysisResult, type ApiDebugInfo } from "./types";

const BROWSER_CACHE_VERSION = "v4-fast";
const BROWSER_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export async function analyzeGerman(query: string, signal?: AbortSignal): Promise<AnalysisResult> {
  const cached = readBrowserCache(query);
  if (cached) {
    return { ...cached, cacheHit: true, cacheLayer: "browser" };
  }

  try {
    await debounceRequest(signal);

    const response = await fetch("/api/analyze", {
      method: "POST",
      headers: {
        "content-type": "application/json"
      },
      body: JSON.stringify({ query, outputLanguage: "de" }),
      signal
    });

    const contentType = response.headers.get("content-type") ?? "";
    if (!contentType.includes("application/json")) {
      throw new ApiDebugError({
        code: "NON_JSON_RESPONSE",
        message: "Der Server hat keine JSON-Antwort geliefert.",
        details: `HTTP ${response.status}. Content-Type: ${contentType || "fehlt"}`,
        status: response.status
      });
    }

    const payload = (await response.json()) as AnalysisResult | ApiDebugInfo;

    if (!response.ok) {
      throw new ApiDebugError(payload as ApiDebugInfo);
    }

    const result = payload as AnalysisResult;
    writeBrowserCache(query, result);
    return result;
  } catch (error) {
    if (signal?.aborted || (error instanceof DOMException && error.name === "AbortError")) {
      throw error;
    }

    if (error instanceof ApiDebugError) {
      throw error;
    }

    if (isLikelyLocalDevError(error)) {
      await new Promise((resolve) => window.setTimeout(resolve, 420));
      return createDemoResult(query);
    }

    throw new ApiDebugError({
      code: "NETWORK_ERROR",
      message: "Die Anfrage konnte nicht abgeschlossen werden.",
      details: error instanceof Error ? error.message : String(error)
    });
  }
}

function debounceRequest(signal?: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Request aborted", "AbortError"));
      return;
    }

    const timeout = window.setTimeout(resolve, 220);
    signal?.addEventListener(
      "abort",
      () => {
        window.clearTimeout(timeout);
        reject(new DOMException("Request aborted", "AbortError"));
      },
      { once: true }
    );
  });
}

function cacheKey(query: string) {
  return `de-helper:${BROWSER_CACHE_VERSION}:${query.trim().toLocaleLowerCase("de-DE")}`;
}

function readBrowserCache(query: string) {
  try {
    const raw = window.localStorage.getItem(cacheKey(query));
    if (!raw) return null;

    const entry = JSON.parse(raw) as { savedAt: number; result: AnalysisResult };
    if (!entry.savedAt || Date.now() - entry.savedAt > BROWSER_CACHE_TTL_MS) {
      window.localStorage.removeItem(cacheKey(query));
      return null;
    }

    return entry.result;
  } catch {
    return null;
  }
}

function writeBrowserCache(query: string, result: AnalysisResult) {
  try {
    window.localStorage.setItem(cacheKey(query), JSON.stringify({ savedAt: Date.now(), result }));
  } catch {
    // Storage may be disabled; the Worker cache still remains available.
  }
}

function isLikelyLocalDevError(error: unknown) {
  if (import.meta.env.DEV) return true;

  if (error instanceof ApiDebugError) {
    return error.info.status === 404 || error.info.code === "NON_JSON_RESPONSE";
  }

  return false;
}
